'use client';
import React, { useState } from 'react';
import { Search, Filter, Eye, Pencil, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import SeverityBadge from '@/components/ui/SeverityBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import { RiskEvent, Category, Severity, Status } from '@/lib/riskData';

const categoryColors: Record<Category, string> = {
  Sanctions: 'text-danger bg-danger/10',
  Cyber: 'text-info bg-info/10',
  Maritime: 'text-primary bg-primary/10',
  'Supply Chain': 'text-warning bg-warning/10',
  'Regional Instability': 'text-orange-400 bg-orange-500/10',
  Energy: 'text-purple-400 bg-purple-500/10',
};

const ITEMS_PER_PAGE = 8;

interface RiskEventTableProps {
  events: RiskEvent[];
  onEventsChange: (events: RiskEvent[]) => void;
  isAdmin: boolean;
}

export default function RiskEventTable({ events, onEventsChange, isAdmin }: RiskEventTableProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortCol, setSortCol] = useState<'severity' | 'escalationProb' | 'confidence' | ''>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<Category | 'All'>('All');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<RiskEvent>>({});

  const severityOrder: Record<Severity, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };

  const filtered = events.filter((e) => {
    const matchSearch = e.event.toLowerCase().includes(search.toLowerCase()) ||
      e.region.toLowerCase().includes(search.toLowerCase()) ||
      e.sector.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'All' || e.category === categoryFilter;
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (sortCol === 'severity') return sortDir === 'desc' ? severityOrder[b.severity] - severityOrder[a.severity] : severityOrder[a.severity] - severityOrder[b.severity];
    if (sortCol === 'escalationProb') return sortDir === 'desc' ? b.escalationProb - a.escalationProb : a.escalationProb - b.escalationProb;
    if (sortCol === 'confidence') return sortDir === 'desc' ? b.confidence - a.confidence : a.confidence - b.confidence;
    return 0;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paged = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const toggleRow = (id: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = paged.length > 0 && paged.every(e => selectedRows.has(e.id));
  const toggleAll = () => {
    if (allSelected) setSelectedRows(prev => { const next = new Set(prev); paged.forEach(e => next.delete(e.id)); return next; });
    else setSelectedRows(prev => { const next = new Set(prev); paged.forEach(e => next.add(e.id)); return next; });
  };

  const startEdit = (event: RiskEvent) => {
    setEditingId(event.id);
    setEditForm({ ...event });
  };

  const saveEdit = () => {
    if (!editingId) return;
    const updated = events.map(e => e.id === editingId ? { ...e, ...editForm } : e);
    onEventsChange(updated);
    setEditingId(null);
    setEditForm({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const deleteEvent = (id: string) => {
    onEventsChange(events.filter(e => e.id !== id));
    setSelectedRows(prev => { const next = new Set(prev); next.delete(id); return next; });
  };

  const deleteSelected = () => {
    onEventsChange(events.filter(e => !selectedRows.has(e.id)));
    setSelectedRows(new Set());
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <ChevronUp size={10} className="opacity-20" />;
    return sortDir === 'desc' ? <ChevronDown size={10} className="text-primary" /> : <ChevronUp size={10} className="text-primary" />;
  };

  const categories: Array<Category | 'All'> = ['All', 'Sanctions', 'Cyber', 'Maritime', 'Supply Chain', 'Regional Instability', 'Energy'];

  return (
    <div className="card-elevated rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
        <div>
          <p className="text-sm font-semibold text-foreground">Active Risk Events</p>
          <p className="text-xs text-muted-foreground">{filtered.length} events · sorted by escalation risk</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search events…"
              className="bg-input border border-border rounded-md pl-8 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring w-44"
            />
          </div>
          <div className="relative group">
            <button className="flex items-center gap-1.5 border border-border rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <Filter size={12} />
              {categoryFilter !== 'All' ? categoryFilter : 'Category'}
            </button>
            <div className="absolute right-0 top-full mt-1 z-20 hidden group-hover:block bg-card border border-border rounded-lg shadow-card-lg py-1 min-w-[160px]">
              {categories.map((cat) => (
                <button
                  key={`filter-${cat}`}
                  onClick={() => { setCategoryFilter(cat); setPage(1); }}
                  className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                    categoryFilter === cat ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedRows.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/10 border-b border-primary/20 animate-fade-in">
          <span className="text-xs font-medium text-primary">{selectedRows.size} selected</span>
          <button className="text-xs text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1 transition-colors">
            Export
          </button>
          <button className="text-xs text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1 transition-colors">
            Generate Report
          </button>
          {isAdmin && (
            <button
              onClick={deleteSelected}
              className="text-xs text-danger hover:text-danger/80 border border-danger/30 rounded px-2 py-1 transition-colors"
            >
              Delete Selected
            </button>
          )}
          <button onClick={() => setSelectedRows(new Set())} className="text-xs text-muted-foreground hover:text-foreground ml-auto">
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="w-8 px-4 py-3">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-primary w-3 h-3" />
              </th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Event</th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Category</th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('severity')}>
                <span className="flex items-center gap-1">Severity <SortIcon col="severity" /></span>
              </th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Region</th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('confidence')}>
                <span className="flex items-center gap-1">Confidence <SortIcon col="confidence" /></span>
              </th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('escalationProb')}>
                <span className="flex items-center gap-1">Esc. Prob. <SortIcon col="escalationProb" /></span>
              </th>
              <th className="px-3 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-3 py-3 text-right font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center">
                  <p className="text-sm font-medium text-foreground mb-1">No risk events match your filters</p>
                  <p className="text-xs text-muted-foreground">Try adjusting your search or category filter</p>
                </td>
              </tr>
            ) : (
              paged.map((event, idx) => (
                editingId === event.id ? (
                  <tr key={event.id} className="border-b border-border bg-primary/5">
                    <td className="px-4 py-2" colSpan={9}>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <input
                          value={editForm.event || ''}
                          onChange={e => setEditForm(f => ({ ...f, event: e.target.value }))}
                          className="col-span-2 bg-input border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          placeholder="Event title"
                        />
                        <input
                          value={editForm.region || ''}
                          onChange={e => setEditForm(f => ({ ...f, region: e.target.value }))}
                          className="bg-input border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          placeholder="Region"
                        />
                        <input
                          value={editForm.sector || ''}
                          onChange={e => setEditForm(f => ({ ...f, sector: e.target.value }))}
                          className="bg-input border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          placeholder="Sector"
                        />
                        <select
                          value={editForm.severity || 'Medium'}
                          onChange={e => setEditForm(f => ({ ...f, severity: e.target.value as Severity }))}
                          className="bg-input border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none"
                        >
                          {['Critical', 'High', 'Medium', 'Low'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select
                          value={editForm.status || 'Monitoring'}
                          onChange={e => setEditForm(f => ({ ...f, status: e.target.value as Status }))}
                          className="bg-input border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none"
                        >
                          {['Escalating', 'Critical', 'Monitoring', 'Resolved', 'Detected', 'Classified'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="flex items-center gap-1 bg-primary text-primary-foreground rounded px-3 py-1 text-xs font-medium hover:opacity-90">
                          <Check size={11} /> Save
                        </button>
                        <button onClick={cancelEdit} className="flex items-center gap-1 border border-border rounded px-3 py-1 text-xs text-muted-foreground hover:bg-muted">
                          <X size={11} /> Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={event.id}
                    className={`border-b border-border transition-colors hover:bg-muted/40 ${
                      selectedRows.has(event.id) ? 'bg-primary/5' : idx % 2 === 0 ? '' : 'bg-muted/10'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedRows.has(event.id)} onChange={() => toggleRow(event.id)} className="accent-primary w-3 h-3" />
                    </td>
                    <td className="px-3 py-3 max-w-[240px]">
                      <p className="text-foreground font-medium leading-snug line-clamp-2">{event.event}</p>
                      <p className="text-muted-foreground mt-0.5">{event.updated} UTC</p>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-md font-medium text-2xs ${categoryColors[event.category]}`}>
                        {event.category}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <SeverityBadge severity={event.severity} size="sm" />
                    </td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{event.region}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 bg-muted rounded-full h-1">
                          <div
                            className="h-1 rounded-full"
                            style={{
                              width: `${event.confidence}%`,
                              backgroundColor: event.confidence >= 85 ? 'var(--success)' : event.confidence >= 70 ? 'var(--warning)' : 'var(--danger)',
                            }}
                          />
                        </div>
                        <span className="text-foreground font-tabular font-medium">{event.confidence}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`font-tabular font-semibold ${
                        event.escalationProb >= 60 ? 'text-danger' : event.escalationProb >= 35 ? 'text-warning' : 'text-success'
                      }`}>
                        {event.escalationProb}%
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <StatusBadge status={event.status} size="sm" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View">
                          <Eye size={12} />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => startEdit(event)}
                              className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                              title="Edit"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              onClick={() => deleteEvent(event.id)}
                              className="p-1.5 rounded hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={13} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={`page-${p}`}
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                  p === page ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}